/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ProductRefiner } from '@msdyn365-commerce/retail-proxy';
import { getRefinersByTextAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { parseSearchData } from './utilities/input-data-parser';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';
import { generateCacheKey } from './utilities/utils';

/**
 * Action Input class for the getRefinersByText data action.
 */
export class RefinersByTextInput implements IActionInput {
    public readonly catalogId: number;

    public readonly searchText: string;

    public readonly queryResultSettingsProxy: QueryResultSettingsProxy;

    private readonly apiSettings: ICommerceApiSettings;

    constructor(
        searchText: string,
        catalogId: number,
        queryResultSettingsProxy: QueryResultSettingsProxy,
        apiSettings: ICommerceApiSettings
    ) {
        this.catalogId = catalogId;
        this.searchText = searchText;
        this.queryResultSettingsProxy = queryResultSettingsProxy;
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'SearchText', value: this.searchText },
            { key: 'CatalogId', value: this.catalogId },
            { key: 'QueryResultSettingsCacheKeyHint', value: this.queryResultSettingsProxy.cacheKeyHint }
        );

    public getCacheObjectType = () => 'ProductRefiner';

    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
export const createRefinersByTextInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    const searchInputData = parseSearchData(inputData);
    const catalogId = inputData.config ? Number(inputData.config.catalogId) : 0;

    return new RefinersByTextInput(
        searchInputData.q,
        Number.isNaN(catalogId) ? 0 : catalogId,
        QueryResultSettingsProxy.fromInputData(inputData),
        inputData.requestContext.apiSettings
    );
};

/**
 * Calls the Retail API and returns all refiners by text.
 * @param input
 * @param ctx
 */
export async function getRefinersByTextAction(input: RefinersByTextInput, ctx: IActionContext): Promise<ProductRefiner[]> {
    return getRefinersByTextAsync(
        { callerContext: ctx, queryResultSettings: input.queryResultSettingsProxy.QueryResultSettings },
        input.catalogId,
        input.searchText
    );
}

export const getRefinersByTextActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-refiners-by-text',
    action: <IAction<ProductRefiner[]>>getRefinersByTextAction,
    input: createRefinersByTextInput
});

export default getRefinersByTextActionDataAction;
